import { useEffect, useRef, useState, FormEvent } from "react";

export default function Contacto() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7dq38um3fsfopf23vqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
      } else {
        setError("Hubo un error al enviar. Por favor intenta de nuevo.");
      }
    } catch {
      setError("Error de conexión. Por favor intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-[#0a0a0f] relative overflow-hidden" ref={ref}>
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#1a0a2e]/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-[#c9a84c] text-sm tracking-widest uppercase font-medium mb-4">Hablemos</p>
          <h2 className="font-serif text-4xl md:text-5xl text-white font-bold mb-4">
            Reserva tu <span className="text-[#c9a84c]">Consulta</span>
          </h2>
          <p className="text-white/60 text-base max-w-xl mx-auto">
            Cuéntame qué necesitas y te responderé en menos de 24 horas para coordinar tu sesión.
          </p>
        </div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start transition-all duration-1000 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          
          {/* Left - Info */}
          <div>
            <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/38f549e75e120f2f71303cb808a7200a.jpg"
                alt="Consulta de tarot"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="space-y-5">
              {[
                { icon: "ri-whatsapp-line", title: "WhatsApp", value: "+34 673 41 00 05", color: "text-green-400" },
                { icon: "ri-mail-line", title: "Email", value: "brujoalbert666@gmail.com", color: "text-[#c9a84c]" },
                { icon: "ri-instagram-line", title: "Instagram", value: "@albertvidente", color: "text-pink-400" },
                { icon: "ri-time-line", title: "Disponibilidad", value: "Lunes a Domingo · 9:00 - 21:00", color: "text-[#c9a84c]" },
                { icon: "ri-translate-2", title: "Idiomas", value: "Español · English · Polski", color: "text-[#c9a84c]" },
              ].map((item) => (
                <div key={item.title} className="flex items-center gap-4 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1a0a2e] rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-lg ${item.color}`}></i>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs">{item.title}</p>
                    <p className="text-white text-sm font-medium">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-[#0f0f1a] border border-white/10 rounded-2xl p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-[#c9a84c]/10 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-[#c9a84c]"></i>
                </div>
                <h3 className="text-white font-serif text-2xl font-bold mb-2">¡Mensaje Enviado!</h3>
                <p className="text-white/60 text-sm">Te responderé en menos de 24 horas. ¡Gracias por confiar en mí!</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-2 border border-[#c9a84c]/40 text-[#c9a84c] text-sm rounded-full hover:bg-[#c9a84c] hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Enviar otro mensaje
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contacto-form"
                className="space-y-5"
              >
                <h3 className="text-white font-serif text-xl font-bold mb-6">Cuéntame tu consulta</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/60 text-xs mb-1.5 block">Nombre *</label>
                    <input
                      type="text"
                      name="nombre"
                      required
                      placeholder="Tu nombre"
                      className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 placeholder-white/30 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-white/60 text-xs mb-1.5 block">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="tu@email.com"
                      className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 placeholder-white/30 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/60 text-xs mb-1.5 block">Servicio de interés</label>
                  <select
                    name="servicio"
                    className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 transition-colors cursor-pointer"
                  >
                    <option value="" className="bg-[#0f0f1a]">Selecciona un servicio</option>
                    <option value="Lectura de Tarot Completa" className="bg-[#0f0f1a]">Lectura de Tarot Completa - €65</option>
                    <option value="Videncia Personal" className="bg-[#0f0f1a]">Videncia Personal - €45</option>
                    <option value="Tarot del Amor" className="bg-[#0f0f1a]">Tarot del Amor - €50</option>
                    <option value="Carta Astral + Tarot" className="bg-[#0f0f1a]">Carta Astral + Tarot - €85</option>
                    <option value="Orientación Laboral" className="bg-[#0f0f1a]">Orientación Laboral - €50</option>
                    <option value="Limpieza Energética" className="bg-[#0f0f1a]">Limpieza Energética - €40</option>
                  </select>
                </div>

                <div>
                  <label className="text-white/60 text-xs mb-1.5 block">Tu consulta *</label>
                  <textarea
                    name="mensaje"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Cuéntame brevemente qué situación quieres consultar..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/5 border border-white/15 text-white text-sm rounded-lg px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 placeholder-white/30 transition-colors resize-none"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount >= 500 ? "text-red-400" : "text-white/30"}`}>
                    {charCount}/500
                  </p>
                </div>

                {error && (
                  <p className="text-red-400 text-xs bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-2">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading || charCount > 500}
                  className="w-full py-4 bg-[#c9a84c] text-black font-bold text-sm rounded-full hover:bg-[#e0c06a] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Enviando..." : "Enviar Consulta"}
                </button>

                <p className="text-white/30 text-xs text-center">
                  Tu información es completamente confidencial y nunca será compartida.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
